import { observer } from "mobx-react-lite";
import { PropsWithChildren, useEffect, useLayoutEffect } from "react";
import { useAppData } from "../RootStoreProvider";
import { getAllSearchParams } from "helpers/getAllSearchParams";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { saveToStorage } from "services/storage";
import { AppRoutes } from "enums/routes.enum";
import { useFbConfigData } from "../hooks";

export const FlowWrapper = observer(({ children }: PropsWithChildren) => {
    const navigate = useNavigate();
    const { pathname, hash } = useLocation();
    const { amplitudeUserId, abTestData: sessionAbTestData, setAbTestData: setSessionAbTestData, setAmpliIDLoading } = useAppData();
    const { abTestData } = useFbConfigData();
    const [searchParams] = useSearchParams();
    const allQueryParams = getAllSearchParams(searchParams);
    const {
        usid: usidQueryParam,
        // The app always runs its default flow, so a 'flowOuter' query param
        // is never applied — it's only used below to strip itself out of the
        // URL bar on the first navigation.
        flowOuter: flowOuterQueryParam,
        gclid: gclidQueryParam,
        is_test: isTestQueryParam,
    } = allQueryParams as Record<string, string>;

    useEffect(() => {
        if (isTestQueryParam) {
            sessionStorage.setItem("isTestUser", isTestQueryParam);
        }
    }, [isTestQueryParam]);

    useLayoutEffect(() => {
        if (gclidQueryParam) {
            saveToStorage("gclid", gclidQueryParam);
        }

        const isLegalPages = [AppRoutes.TERMS_OF_USE, AppRoutes.PRIVACY_POLICY, AppRoutes.COOKIE_POLICY].includes(pathname as AppRoutes);

        /*
         *  Here we are visually updating 'usid' search param (usidQueryParam) in
         *  browsers URL bar when page navigation happens or if amplitudeUserId has changed.
         *  With exception of this AmplitudeProvider module, 'usid' search param should not be used
         *  as "source of truth" elsewhere inside application logic, use amplitudeUserId instead.
         */
        if (!isLegalPages && amplitudeUserId && amplitudeUserId !== usidQueryParam && abTestData) {
            const newQueryParams = { ...allQueryParams };

            if (flowOuterQueryParam) {
                delete (newQueryParams as any).flowOuter;
            }

            newQueryParams["usid"] = amplitudeUserId;

            navigate(
                {
                    pathname,
                    hash,
                    search: createSearchParams(newQueryParams).toString(),
                },
                { replace: true, preventScrollReset: true }
            );
        }

        /* Send amplitude page navigation event on last update */
        if (amplitudeUserId && amplitudeUserId === usidQueryParam && abTestData) {
            /* Saving AB test data into store */
            if (!sessionAbTestData && abTestData) {
                setSessionAbTestData(abTestData);
            }

            setAmpliIDLoading(false);
        }
    }, [pathname, amplitudeUserId, usidQueryParam, flowOuterQueryParam, gclidQueryParam, abTestData]);

    return <>{children}</>;
});
